import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fileUrl } from '@/lib/api';
import { useReveal } from '@/lib/motion';
import SectionBackground from '@/components/SectionBackground';
import GradientMesh from '@/components/GradientMesh';

// v3.19 — Experience card description map (introduced in v3.13 for the
// modal; re-purposed in v3.15 as the BACK FACE content for the 3D card
// flip; v3.16 unified all 8 cards through the flip branch; v3.19 adds
// coverage for production DB titles and keeps preview-era titles as
// defensive fallbacks).
// ---------------------------------------------------------------------
// Keyed by NORMALIZED card title (uppercase + trim + collapsed
// whitespace). Title-based keys are intentional (not UUIDs) so the
// mapping is human-readable and survives admin card delete/re-create.
//
// IMPORTANT: This map intentionally carries MULTIPLE keys per logical
// card to absorb admin title renames in either direction without
// re-deploying. For example, the "Studio IX" card has been called both
// "STUDIO IX COLLECTIVE" (production) and "STUDIO IX GLAM JAM"
// (preview); both keys resolve to the same verbatim description. If
// you intentionally split one card into two distinct concepts, give
// the new card its own unique description and a new map entry.
//
// Descriptions are verbatim copy from product. Do not edit, paraphrase,
// or shorten without product sign-off.
const EXPERIENCE_DESCRIPTIONS = {
  'LAS VEGAS LIVE':
    "Las Vegas Live is SEICon's live broadcast and content platform, capturing the energy, insights, and personalities shaping the future of sports, entertainment, hospitality, and technology. Through executive interviews, athlete conversations, thought-leadership segments, and real-time event coverage, Las Vegas Live extends the reach of SEICon beyond the Bellagio, creating year-round digital engagement and a powerful storytelling engine for partners, sponsors, and attendees.",
  // Athletes Abroad — two key variants for resilience against admin
  // rename. Production currently uses "ATHLETES ABROAD SUMMIT";
  // preview-era used "ATHLETES ABROAD". Same description applies.
  'ATHLETES ABROAD':
    'Athletes Abroad brings together current and former athletes, coaches, executives, and emerging leaders to explore the expanding global landscape of sport. Focused on international competition, career development, cultural exchange, and business opportunities, the platform highlights how sports continue to serve as a bridge between nations, industries, and communities.',
  'ATHLETES ABROAD SUMMIT':
    'Athletes Abroad brings together current and former athletes, coaches, executives, and emerging leaders to explore the expanding global landscape of sport. Focused on international competition, career development, cultural exchange, and business opportunities, the platform highlights how sports continue to serve as a bridge between nations, industries, and communities.',
  'INDOOR GOLF SUMMIT':
    "Indoor Golf Summit convenes the innovators driving the explosive growth of golf technology, simulator experiences, entertainment venues, coaching platforms, and facility development. From professional training environments to consumer-driven social golf concepts, the summit explores the convergence of sport, technology, real estate, hospitality, and investment shaping golf's next era.",
  'LEIGH STEINBERG HEALTH & WELLNESS PAVILION':
    'Leigh Steinberg Health & Wellness Pavilion serves as a destination for conversations surrounding athlete health, mental wellness, performance optimization, longevity, and healthcare innovation. Curated with industry leaders, medical professionals, researchers, and athletes, the pavilion showcases emerging technologies, best practices, and collaborative solutions designed to improve lives both on and off the field.',
  // Studio IX — two key variants. Production uses "STUDIO IX COLLECTIVE";
  // preview-era used "STUDIO IX GLAM JAM". Same description applies.
  'STUDIO IX COLLECTIVE':
    'Studio IX is a curated environment dedicated to the intersection of creativity, storytelling, media production, and emerging technologies. Bringing together creators, brands, agencies, athletes, and investors, Studio IX explores how content, immersive experiences, and digital engagement are transforming the future of audience connection and cultural influence.',
  'STUDIO IX GLAM JAM':
    'Studio IX is a curated environment dedicated to the intersection of creativity, storytelling, media production, and emerging technologies. Bringing together creators, brands, agencies, athletes, and investors, Studio IX explores how content, immersive experiences, and digital engagement are transforming the future of audience connection and cultural influence.',
  'ZERO LABS':
    'Zero Labs highlights the future of sustainable mobility, advanced engineering, and next-generation vehicle innovation. Through executive discussions, technology demonstrations, and strategic partnerships, Zero Labs showcases how electrification, intelligent systems, and modern manufacturing are redefining transportation while preserving iconic automotive heritage.',
  // Creator Studio Expo — two key variants. Production uses "CREATOR
  // STUDIO EXPO"; preview-era used "INNOVATOR CREATOR STUDIO". Same
  // description applies.
  'CREATOR STUDIO EXPO':
    'Creator Studio Expo is the experiential centerpiece of SEICon, bringing together creators, media companies, technology providers, brands, universities, and sports organizations in a highly interactive environment. Featuring live broadcasts, podcast production, AI demonstrations, immersive technologies, and emerging media platforms, the Expo provides attendees with direct access to the tools, talent, and innovations shaping the future creator economy.',
  'INNOVATOR CREATOR STUDIO':
    'Creator Studio Expo is the experiential centerpiece of SEICon, bringing together creators, media companies, technology providers, brands, universities, and sports organizations in a highly interactive environment. Featuring live broadcasts, podcast production, AI demonstrations, immersive technologies, and emerging media platforms, the Expo provides attendees with direct access to the tools, talent, and innovations shaping the future creator economy.',
  // v3.16 — INVESTOR FAST TRACK joins the flip-wired branch. Naturally
  // takes idx=3 in the live grid → animation-delay 1.20s, landing
  // cleanly in the existing 0.4s-increment stagger.
  'INVESTOR FAST TRACK':
    "SEICON FAST TRACK is the invitation-only executive series where the world's top sports, entertainment, and innovation leaders meet to make deals — not attend a conference. Produced year-round across North America, Europe, Asia, and the Middle East, every one-day gathering is engineered for one outcome: real partnerships, real agreements, real momentum. Thought leaders. Innovators. Investors. Media. In a room together, on purpose, with the access most executives spend a career chasing. This is the front of the line. If you need to be in the conversations shaping the future of the industry, Fast Track is where the future is already being decided.",
};

// Normalize a card title for lookup in EXPERIENCE_DESCRIPTIONS.
// Uppercase + trim + collapse internal whitespace so minor casing or
// spacing edits in admin still match.
const normalizeExperienceKey = (title) => String(title || '').trim().toUpperCase().replace(/\s+/g, ' ');

export default function ExperiencesSection({ experiences = [], section = {}, meta = {}, motion = {} }) {
  const navigate = useNavigate();
  const headerReveal = useReveal();
  const gridReveal = useReveal({ threshold: 0.05 });
  const meshEnabled = motion?.wow?.gradient_mesh_experiences !== false;
  const staggerEnabled = motion?.reveals?.experiences_stagger !== false;

  // v3.15 — Single shared "flipped card" slot for the section. Storing
  // a single id (rather than per-card boolean state) gives us the
  // "single-card-at-a-time" behavior for free: flipping a new card
  // automatically flips any other one back to its front.
  const [flippedId, setFlippedId] = useState(null);

  const eyebrow = meta.eyebrow || 'ONE EVENT.';
  const headline = meta.headline || 'MULTIPLE POWERFUL EXPERIENCES.';
  const learnMoreLabel = meta.learn_more_label || 'Learn More';
  const comingSoonBadge = meta.coming_soon_badge || 'Coming Soon';
  const buttonLabel = meta.button_label || 'View All Experiences';
  const buttonUrl = meta.button_url || '/experiences';
  // v3.14 — Admin-controlled visibility for the "View All Experiences" CTA.
  // Backed by sections_meta.experiences.hide_view_all_button. Defaults to
  // HIDDEN (true) — including when the field is missing from the DB —
  // so the CTA stays off until an admin explicitly unchecks the toggle
  // in the Experiences tab > Section Labels > "Hide View All Experiences
  // Button" checkbox.
  const hideViewAllButton = meta.hide_view_all_button !== false;

  // Mobile polish (Prompt A) — Experiences band identity locked to bg-canvas (#F4EFE7).
  // Override only the background COLOR value; preserves admin image/video/overlay settings if used.
  const expSection = { ...section, background_type: 'color', background_value: '#F4EFE7' };
  const isDark = (expSection.background_type || 'color') === 'color'
    ? /^#0[0-9A-F]/i.test(expSection.background_value || '#0A0A0C')
    : (expSection.overlay_opacity || 0) > 0.4;
  const titleColor = isDark ? 'text-white' : 'text-ink-primary';

  const handleButton = () => {
    if (buttonUrl && buttonUrl.startsWith('http')) {
      window.open(buttonUrl, '_blank', 'noopener,noreferrer');
    } else if (buttonUrl && buttonUrl.startsWith('#')) {
      const el = document.querySelector(buttonUrl);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(buttonUrl || '/experiences');
    }
  };

  return (
    <SectionBackground section={expSection} fallbackColor="#F4EFE7">
      <section id="experiences" className="section-pad relative overflow-hidden" data-testid="section-experiences">
        {meshEnabled && <GradientMesh enabled={meshEnabled} />}
        <div className="container-x relative z-10">
          <div ref={headerReveal.ref} className={`text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}>
            {/* v3.15 — Eyebrow size from sections_meta.experiences.eyebrow_size.
                Defensive validation: integer in [16, 48] only. Falls
                through to 26px default via CSS `var()` fallback. */}
            {(() => {
              const _raw = meta.eyebrow_size;
              const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
              return (
                <p className="eyebrow eyebrow-lg" data-testid="experiences-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>{eyebrow}</p>
              );
            })()}
            <h2 className={`mt-4 font-display font-bold uppercase ${titleColor} leading-[1.05] tracking-tightish text-[36px] md:text-[40px] lg:text-[56px]`} data-testid="experiences-headline">
              {headline}
            </h2>
          </div>

          <div ref={gridReveal.ref} className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {experiences.map((e, idx) => {
              const cardId = e.id || normalizeExperienceKey(e.title) || `exp-idx-${idx}`;
              return (
                <ExperienceCard
                  key={e.id || idx}
                  exp={e}
                  idx={idx}
                  cardId={cardId}
                  isFlipped={flippedId === cardId}
                  onCardFlip={() => setFlippedId((prev) => (prev === cardId ? null : cardId))}
                  revealed={gridReveal.revealed}
                  simple={gridReveal.simple}
                  staggerEnabled={staggerEnabled}
                  learnMoreLabel={learnMoreLabel}
                  comingSoonBadge={comingSoonBadge}
                />
              );
            })}
          </div>

          <div className="mt-10 text-center">
            {!hideViewAllButton && (
              <button className={`btn-secondary ${isDark ? '!border-white !text-white hover:!bg-white hover:!text-card-dark' : ''}`} onClick={handleButton} data-testid="view-all-experiences">
                {buttonLabel}
              </button>
            )}
          </div>
        </div>
      </section>
    </SectionBackground>
  );
}

function ExperienceCard({ exp, idx, cardId, isFlipped, onCardFlip, revealed, simple, staggerEnabled, learnMoreLabel, comingSoonBadge }) {
  const image = fileUrl(exp.image) || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1000&q=80&auto=format&fit=crop';
  const accent = exp.color || '#E8500A';
  const delay = staggerEnabled ? idx * 80 : 0;
  const isComingSoon = !!exp.coming_soon;
  const hideLearnMore = !!exp.hide_learn_more;

  // v3.20 — Task 3A. Admin-editable back-face description with strict
  // absent-vs-empty semantics — the legacy fallback lookup and the `||`
  // operator are intentionally NOT used together, since `||` would
  // collapse an intentionally saved "" into the legacy fallback. Three
  // branches:
  //   1. `back_description` key is absent from the record → render the
  //      legacy hardcoded fallback ONLY when the mapped fallback text
  //      is meaningful (trimmed length > 0). Titles with no map entry
  //      (or a blank one) render no <p> at all — no residual margin, no
  //      gap between the tile title and where the paragraph would sit.
  //   2. Key is present as a non-empty string (after trim) → render
  //      the ORIGINAL saved value verbatim ({bd}, not {bd.trim()}), so
  //      operators can intentionally include leading/trailing spaces
  //      around real words and see them preserved. `trim()` is used
  //      ONLY to decide whether meaningful text exists.
  //   3. Key is present but blank ("", whitespace-only, or defensively
  //      null) → render NOTHING (no <p>, no fallback, no gap) so
  //      operators can intentionally blank out a tile back.
  const hasBackDescription = Object.prototype.hasOwnProperty.call(exp, 'back_description');
  const legacyDescription = EXPERIENCE_DESCRIPTIONS[normalizeExperienceKey(exp.title)] || '';
  let backDescriptionNode = null;
  if (hasBackDescription) {
    const bd = exp.back_description;
    if (typeof bd === 'string' && bd.trim().length > 0) {
      backDescriptionNode = (
        <p className="mt-3 font-body text-[13px] text-white/85 leading-relaxed" data-testid="experience-card-back-description">
          {bd}
        </p>
      );
    }
    // else "", whitespace-only, or null → intentional blank → render nothing
  } else if (legacyDescription.trim().length > 0) {
    // Key absent AND legacy fallback text is meaningful → render it.
    // If the fallback text is blank (missing map entry or empty
    // string), render nothing at all — avoids an empty <p> with a
    // residual mt-3 margin-top opening a gap under the title.
    backDescriptionNode = (
      <p className="mt-3 font-body text-[13px] text-white/85 leading-relaxed" data-testid="experience-card-back-description">
        {legacyDescription}
      </p>
    );
  }

  // Front-face inner content. Markup is byte-identical to the v3.15
  // front face — same image, gradients, h3 title, copy paragraph,
  // Coming Soon / Learn More badge, and accent border.
  const cardFrontInner = (
    <>
      <img src={image} alt={exp.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity duration-[250ms]" />
      <div className="absolute inset-0 transition-opacity duration-[250ms]" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%)' }} />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms]" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 20%, rgba(0,0,0,0.92) 100%)' }} />
      <div className="absolute inset-0 flex flex-col justify-end p-6 z-[1]">
        <h3 className="font-display font-bold uppercase text-white text-[20px] leading-[1.1] tracking-tightish">{exp.title}</h3>
        <p className="mt-2 font-body text-[13px] text-white/70 leading-snug">{exp.copy}</p>
        {hideLearnMore ? null : isComingSoon ? (
          <span className="mt-4 inline-block self-start font-body font-bold text-[10px] uppercase tracking-wider2 bg-brand-orange text-white px-2.5 py-1 rounded" data-testid="exp-coming-soon">
            {comingSoonBadge}
          </span>
        ) : (
          <span className="mt-4 font-body font-semibold text-[12px] uppercase tracking-wider2" style={{ color: accent }}>
            {learnMoreLabel} <span className="arrow-nudge">→</span>
          </span>
        )}
      </div>
      <span
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[250ms]"
        style={{ backgroundColor: accent }}
      />
    </>
  );

  // v3.16 — Unified flip-wired branch. ALL 8 Experiences cards (the
  // previous 7 plus INVESTOR FAST TRACK) render through this single
  // branch — no conditional exclusion. The card is a real <button> so
  // it's keyboard-activatable (Enter/Space). Three nested wrappers
  // separate concerns so transforms don't fight:
  //   .exp-card-bob   → outer; static drop-shadow + idle bob keyframe
  //   .exp-card-hover → inner; desktop hover-lift translateY (no-op on touch)
  //   <button>        → the 3D layer (perspective); houses the two faces
  // The flip transform lives on .exp-card-flip-inner (a descendant of
  // the button), so it composes cleanly with bob + hover-lift.
  const flipAriaLabel = isFlipped
    ? `Flip card back to show ${exp.title} cover`
    : `Flip card to learn more about ${exp.title}`;
  return (
    <div
      className={`exp-card-bob ${isFlipped ? 'is-flipped' : ''} ${simple ? 'reveal-simple' : 'reveal-card'} ${revealed ? 'is-in' : ''}`}
      style={{
        transitionDelay: revealed ? `${delay}ms` : '0ms',
        // Stagger the idle bob so cards never sync. With 8 cards at
        // grid indices 0..7 this yields delays 0.0, 0.4, 0.8, 1.2,
        // 1.6, 2.0, 2.4, 2.8s — INVESTOR FAST TRACK (idx=3) lands
        // cleanly in the 1.20s slot.
        animationDelay: `${(idx * 0.4).toFixed(2)}s`,
      }}
      data-card-id={cardId}
    >
      <div className="exp-card-hover">
        <button
          type="button"
          onClick={onCardFlip}
          aria-pressed={isFlipped}
          aria-label={flipAriaLabel}
          className="exp-card-3d group relative block w-full text-left rounded-md bg-bg-elevated aspect-[4/3] md:aspect-[3/4] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
          data-testid="experience-card"
          data-flipped={isFlipped ? 'true' : 'false'}
        >
          <div className={`exp-card-flip-inner ${isFlipped ? 'is-flipped' : ''}`}>
            {/* Front face — visually identical to v3.15's card front. */}
            <div className="exp-card-face exp-card-face-front bg-bg-elevated" data-testid="experience-card-front">
              {cardFrontInner}
            </div>
            {/* Back face — dark panel with title + verbatim description.
                Outer face is overflow:hidden (rounded-corner clip);
                inner pad-wrapper (.exp-card-back-scroll) is the scroll
                container so long descriptions stay readable on shorter
                mobile cards. iOS momentum scrolling + scroll
                containment are configured in index.css. */}
            <div className="exp-card-face exp-card-face-back bg-card-dark" data-testid="experience-card-back" aria-hidden={!isFlipped}>
              <div className="exp-card-back-scroll absolute inset-0">
                <div className="exp-card-back-body p-6">
                  <h3 className="font-display font-bold uppercase text-white text-[20px] leading-[1.1] tracking-tightish" data-testid="experience-card-back-title">
                    {exp.title}
                  </h3>
                  {backDescriptionNode}
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
